import { TermFormFieldComment } from './term-form-field-comment';
import { EmployeeConsiderationPayer } from './employee-consideration-payer.enum';

export const YES_VALUE = 'Yes';
export const NO_VALUE = 'No';
export const YES_NO_OPTIONS = [NO_VALUE, YES_VALUE];

export interface PropertyPurchased {
    practicePropertyPurchased?: string | null;
    practiceRealPropertyInclude?: string | null;
}



export interface WorkingCapitalHoldback {
    value?: number | null;
    monthsHold?: number | null;
    considerationForEmployees?: number | null;
    buyerConsiderationForEmployees?: number | null;
    fairValue?: number | null;
    fairValueExplanation?: string | null;
}

export const DEFERRED_FIXED_PAYMENT_COLUMN_LABELS = {
    paymentTiming: 'Payment Timing',
    amount: 'Amount',
    annualizedInterestAccrualRate: 'Annualized Interest Accrual Rate',
    contingent: 'Contingent?',
    contingencyDependentVariable: 'Contingency Dependent Variable ("ContDV")',
    contDvMinimumValue: 'ContDV minimum value',
    employmentContingency: 'Employment Contingency',
    tailPeriodForEmployment: 'Tail Period for Employment',
} as const;


export const DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS = {
    paymentTiming: 'Payment Timing',
    type: 'Type',
    valueAtClosing: 'Value at Closing',
    calculationDependentVariable: 'Calculation Dependent Variable ("CalcDV")',
    calcDvBaselineValue: 'CalcDV Baseline Value',
    calculationMultiple: 'Calculation Multiple',
    maximumPayment: 'Maximum Payment',
    employmentContingency: 'Employment Contingency',
    tailPeriodForEmploymentContingency: 'Tail period for Employment Contingency',
    penaltyMultiple: 'Penalty Multiple',
    distributionPeriodicity: 'Distribution Periodicity',
} as const;


export const JV_EQUITY_COLUMN_LABELS = {
    valueAtClosing: 'Value at Closing',
    paymentTiming: 'Payment Timing',
    calculationDependentVariable: 'Calculation Dependent Variable ("CalcDV")',
    calcDvBaselineValue: 'CalcDV Baseline Value',
    calculationMultiple: 'Calculation Multiple',
    employmentContingency: 'Employment Contingency',
    tailPeriodForEmploymentContingency: 'Tail period for Employment Contingency',
    penaltyMultiple: 'Penalty Multiple',
    distributionPeriodicity: 'Distribution Periodicity',
} as const;


export const CORPORATE_EQUITY_COMMON_COLUMN_LABELS = {
    valueAtClosing: 'Value at Closing',
    expectedTimingToRecap: 'Expected Timing to Recap',
    expectedPayoutAsMultipleOfCurrentValue: 'Expected Payout, as multiple of current value',
} as const;

export const CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS = {
    valueAtClosing: 'Value at Closing',
    annualDividendRate: 'Annual Dividend Rate',
    dividendPaymentTiming: 'Dividend Payment Timing',
    compoundingFrequency: 'Compounding Frequency',
    expectedTimingToRecap: 'Expected Timing to Recap',
    convertibleIntoCommon: 'Convertible into Common',
    conversionRatio: 'Conversion Ratio',
} as const;


export interface DeferredFixedPaymentRow {
    paymentTiming?: number | null;
    amount?: number | null;
    annualizedInterestAccrualRate?: string | null;
    contingent?: string | null;
    contingencyDependentVariableValue?: string | null;
    contDvMinimumValue?: number | null;
    employmentContingency?: string | null;
    tailPeriodForEmployment?: string | null;
    considerationForEmployees?: number | null;
    buyerConsiderationForEmployees?: number | null;
    fairValue?: number | null;
    fairValueExplanation?: string | null;
}

export interface DeferredFixedPayments {
    rows: DeferredFixedPaymentRow[];
}

export enum DeferredVariablePaymentType {
    Earnout = 'Earnout',
    Synthetic = 'Synthetic',
}

export interface DeferredVariablePaymentRow {
    paymentTiming?: number | null;
    type?: DeferredVariablePaymentType | null;
    valueAtClosing?: number | null;
    calculationDependentVariable?: string | null;
    calcDvBaselineValue?: number | null;
    calculationMultiple?: string | null;
    maximumPayment?: string | null;
    employmentContingency?: string | null;
    tailPeriodForEmploymentContingency?: string | null;
    penaltyMultiple?: string | null;
    distributionPeriodicity?: string | null;
    considerationForEmployees?: number | null;
    buyerConsiderationForEmployees?: number | null;
    fairValue?: number | null;
    fairValueExplanation?: string | null;
}

export interface DeferredVariablePayments {
    rows: DeferredVariablePaymentRow[];
}

export interface JvEquityRow {
    valueAtClosing?: number | null;
    paymentTiming?: number | null;
    calculationDependentVariable?: string | null;
    calcDvBaselineValue?: number | null;
    calculationMultiple?: string | null;
    employmentContingency?: string | null;
    tailPeriodForEmploymentContingency?: string | null;
    penaltyMultiple?: string | null;
    distributionPeriodicity?: string | null;
    considerationForEmployees?: number | null;
    buyerConsiderationForEmployees?: number | null;
    fairValue?: number | null;
    fairValueExplanation?: string | null;
}

export interface JvEquity {
    row?: JvEquityRow | null;
}

export enum CorporateEquityType {
    Preferred = 'Preferred',
    Common = 'Common',
}

export enum DividendPaymentTiming {
    Monthly = 'Monthly',
    Quarterly = 'Quarterly',
    SemiAnnual = 'SemiAnnual',
    Annual = 'Annual',
    Cumulative = 'Cumulative',
}

export const DIVIDEND_PAYMENT_TIMING_LABELS: Record<DividendPaymentTiming, string> = {
    [DividendPaymentTiming.Monthly]: 'Monthly',
    [DividendPaymentTiming.Quarterly]: 'Quarterly',
    [DividendPaymentTiming.SemiAnnual]: 'Semi-Annual',
    [DividendPaymentTiming.Annual]: 'Annual',
    [DividendPaymentTiming.Cumulative]: 'Cumulative',
};

export enum CompoundingFrequency {
    Monthly = 'Monthly',
    Quarterly = 'Quarterly',
    SemiAnnual = 'SemiAnnual',
    Annual = 'Annual',
    None = 'None',
}

export const COMPOUNDING_FREQUENCY_LABELS: Record<CompoundingFrequency, string> = {
    [CompoundingFrequency.Monthly]: 'Monthly',
    [CompoundingFrequency.Quarterly]: 'Quarterly',
    [CompoundingFrequency.SemiAnnual]: 'Semi-Annual',
    [CompoundingFrequency.Annual]: 'Annual',
    [CompoundingFrequency.None]: 'None',
};

export interface CorporateEquityRow {
    type?: CorporateEquityType | null;
    valueAtClosing?: number | null;
    expectedTimingToRecap?: string | null;
    expectedPayoutAsMultipleOfCurrentValue?: string | null;
    annualDividendRate?: string | null;
    dividendPaymentTiming?: DividendPaymentTiming | null;
    compoundingFrequency?: CompoundingFrequency | null;
    convertibleIntoCommon?: boolean | null;
    conversionRatio?: number | null;
    considerationForEmployees?: number | null;
    buyerConsiderationForEmployees?: number | null;
    fairValue?: number | null;
    fairValueExplanation?: string | null;
}

export interface CorporateEquity {
    rows: CorporateEquityRow[];
}

export interface CashAtCloser {
    cashAtClose?: number | null;
    workingCapitalHoldback?: WorkingCapitalHoldback | null;
    netCashAtCloser?: number | null;
    considerationForEmployees?: number | null;
    buyerConsiderationForEmployees?: number | null;
    fairValue?: number | null;
    fairValueExplanation?: string | null;
}

export interface PurchasePriceSummary {
    cashAtCloser?: CashAtCloser | null;
    deferredFixedPayments?: DeferredFixedPayments | null;
    deferredVariablePayments?: DeferredVariablePayments | null;
    jvEquity?: JvEquity | null;
    corporateEquity?: CorporateEquity | null;
    other?: number | null;
    otherConsiderationForEmployees?: number | null;
    otherBuyerConsiderationForEmployees?: number | null;
    otherFairValue?: number | null;
    otherFairValueExplanation?: string | null;
}

export interface AssumptionOfMajorContracts {
    labContract?: boolean | null;
    labContractExplanation?: string | null;
    operatingLeases?: boolean | null;
    operatingLeasesExplanation?: string | null;
    capitalLeases?: boolean | null;
    capitalLeasesExplanation?: string | null;
    realEstateLease?: boolean | null;
    realEstateLeaseExplanation?: string | null;
}

export interface NonCompetitionRestriction {
    distanceRadiusMiles?: number | null;
    durationMonthsFromClosing?: string | null;
    durationMonthsFromEmployment?: string | null;
}

export interface NonSolicitationRestriction {
    employeesTerm?: string | null;
    employeesTail?: string | null;
    customersTerm?: string | null;
    customersTail?: string | null;
}

export interface GeneralDeferredPaymentsInformation {
    obligorName?: string | null;
    seniorDebt?: string | null;
    allOtherLiabilities?: string | null;
    balanceSheetCash?: string | null;
    ltmEbitdaProForma?: string | null;
}

export interface LeaseTerms {
    termOfLease?: string | null;
    leaseType?: string | null;
    leaseRate?: string | null;
    leaseRateEscalation?: string | null;
    leaseRateUponExtension?: string | null;
}

export interface TermFormLocation {
    locationId: string;
    locationName: string;
    leaseAssumption: 'existing' | 'new' | null;
    termOfLease?: string | null;
    leaseType?: string | null;
    leaseRate?: string | null;
    leaseRateEscalation?: string | null;
    leaseRateUponExtension?: string | null;
}

export interface VendorContractRow {
    id: string;
    tableRowId: number;
    // false = explicit "Payoff at Close" answer; undefined on legacy data = assumed
    isAssumedByBuyer?: boolean | null;
}

export interface SellingOwnerEmploymentAgreementRow {
    id: string;
    tableRowId: number;
    durationMonths: number | null;
    // null/undefined on a saved row = legacy data from before the explicit Yes/No answer (implies "yes")
    isEmployedPostClose?: boolean | null;
}

export interface TermForm {
    loiExpirationDate?: string | null;
    exclusivityPeriodMonth?: number | null;
    propertyPurchased?: PropertyPurchased | null;
    purchasePriceSummary?: PurchasePriceSummary | null;
    nonCompetitionRestriction?: NonCompetitionRestriction | null;
    nonSolicitationRestriction?: NonSolicitationRestriction | null;
    generalDeferredPaymentsInformation?: GeneralDeferredPaymentsInformation | null;
    employeeConsiderationPayer?: EmployeeConsiderationPayer | null;
    vendorContractRows?: VendorContractRow[] | null;
    sellingOwnerEmploymentAgreementRows?: SellingOwnerEmploymentAgreementRow[] | null;
    assumptionOfMajorContracts?: AssumptionOfMajorContracts | null;
    fieldComments?: TermFormFieldComment[];
    locations?: TermFormLocation[] | null;
    isLegalInfoConfirmed?: boolean;
}

export function createEmptyTermForm(): TermForm {
    return {
        loiExpirationDate: null,
        exclusivityPeriodMonth: null,
        propertyPurchased: {
            practicePropertyPurchased: null,
            practiceRealPropertyInclude: null,
        },
        purchasePriceSummary: {
            cashAtCloser: {
                cashAtClose: null,
                workingCapitalHoldback: { value: null, monthsHold: null },
                netCashAtCloser: null,
            },
            deferredFixedPayments: { rows: [] },
            deferredVariablePayments: { rows: [] },
            jvEquity: { row: null },
            corporateEquity: { rows: [] },
            other: null,
        },
        nonCompetitionRestriction: {
            distanceRadiusMiles: null,
            durationMonthsFromClosing: null,
            durationMonthsFromEmployment: null,
        },
        nonSolicitationRestriction: {
            employeesTerm: null,
            employeesTail: null,
            customersTerm: null,
            customersTail: null,
        },
        generalDeferredPaymentsInformation: {
            obligorName: null,
            seniorDebt: null,
            allOtherLiabilities: null,
            balanceSheetCash: null,
            ltmEbitdaProForma: null,
        },
        employeeConsiderationPayer: null,
        vendorContractRows: null,
        locations: [],
        assumptionOfMajorContracts: {
            labContract: null,
            labContractExplanation: null,
            operatingLeases: null,
            operatingLeasesExplanation: null,
            capitalLeases: null,
            capitalLeasesExplanation: null,
            realEstateLease: null,
            realEstateLeaseExplanation: null,
        },
    };
}
